import { useState } from 'react';

const aboutFaqs = [
  {
    q: 'How long has DBN Garage been in business?',
    a: 'DBN Garage brings over 25 years of automotive expertise to every installation. Our team\'s combined experience covers paint correction, vehicle protection, and enhancement for everything from daily drivers to exotic supercars. That depth of experience is why Hartford County\'s most discerning vehicle owners choose DBN Garage.',
  },
  {
    q: 'Are you XPEL certified?',
    a: 'Yes — DBN Garage is a fully certified XPEL installer. XPEL certification requires rigorous training and testing that most shops never complete. This certification means we install XPEL\'s premium PPF products to factory specification, and our customers\' warranties are honored directly through XPEL — the world\'s leading PPF manufacturer.',
  },
  {
    q: 'What makes DBN Garage different from other detailing shops?',
    a: 'DBN Garage is a specialized protection studio — not a general detailing shop. We focus exclusively on paint protection film, ceramic coating, and related services. This specialization means deeper expertise, better tools, a controlled installation environment, and premium product-only sourcing. The result is a measurably higher quality outcome on every vehicle.',
  },
  {
    q: 'Do you work on exotic and luxury vehicles?',
    a: 'Yes — exotic and luxury vehicles are among our most common clients. We regularly work on Ferrari, Lamborghini, Porsche, McLaren, BMW M series, and other premium marques. Our team understands the specific panel geometry and paint sensitivities of exotic vehicles. Every exotic receives the same obsessive preparation and precision we apply to every install.',
  },
  {
    q: 'What are your hours and where are you located?',
    a: 'DBN Garage is located in South Windsor, CT. We operate by appointment to ensure every vehicle receives our full attention — no drop-in queues, no rushed installs. We are available Monday through Saturday. Call (860) 555-0127 or use our online quote form to schedule your free consultation.',
  },
  {
    q: 'Do you offer any guarantees on your work?',
    a: 'Yes. Every installation at DBN Garage includes our workmanship guarantee. If something isn\'t right — a lift edge, a bubble, an issue with the installation — we come back and fix it, full stop. In addition, XPEL PPF carries a 10-year manufacturer warranty and our ceramic coatings carry 2–5 year manufacturer warranties.',
  },
  {
    q: 'Do you install on motorcycles and specialty vehicles?',
    a: 'Yes — we offer custom-cut PPF and ceramic coating for motorcycles, exotics, and specialty vehicles. Motorcycles require hand-templating for fairing and bodywork sections. We work with all major motorcycle makes and custom vehicles. Just bring us your bike and we\'ll design a protection plan specifically for it.',
  },
  {
    q: 'Why does premium vehicle protection matter?',
    a: 'Your vehicle\'s paint is constantly exposed to UV rays, road debris, environmental fallout, and oxidation. Without protection, paint degrades — lowering both appearance and resale value significantly. PPF and ceramic coating from a certified installer like DBN Garage protect your investment permanently, keeping your vehicle looking newer and worth more for years.',
  },
];

export default function AboutFAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="bg-[#080808] py-28" id="faq">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <div className="lg:col-span-2">
            <span className="text-[#FF6B35] text-xs font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
              <span className="w-6 h-px bg-[#FF6B35]" />
              About DBN Garage
            </span>
            <h2 className="font-display font-black text-4xl lg:text-5xl text-white leading-tight mb-6">
              Questions About
              <br />
              <span className="text-[#FF6B35]">Our Studio</span>
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-8">
              Want to know more about the team, our certifications, or what makes DBN Garage different? We love talking about cars — reach out anytime.
            </p>
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-4">
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className="ri-building-line text-[#FF6B35] text-xl" />
              </div>
              <h3 className="text-white font-bold text-base mb-2">Who are the benefits of PPF for?</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                PPF delivers the highest value to new vehicle owners, luxury and exotic car owners, highway commuters, and anyone who wants to preserve resale value. If you love your vehicle and want it to stay flawless — PPF is the single best investment you can make from day one of ownership.
              </p>
            </div>
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className="ri-lightbulb-line text-[#FF6B35] text-xl" />
              </div>
              <h3 className="text-white font-bold text-base mb-2">Benefits of upgrading your vehicle's protection</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Upgrading from no protection to PPF + ceramic coating preserves factory paint, increases resale value by 10–15%, eliminates the cost of paint correction over time, and allows stress-free daily use of vehicles you love. The upgrade pays for itself at the time of resale.
              </p>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-3">
            {aboutFaqs.map((faq, i) => (
              <div key={i}
                className={`rounded-xl border transition-all ${open === i ? 'border-[#FF6B35]/40 bg-[#111111]' : 'border-white/8 bg-[#0E0E0E] hover:border-white/15'}`}>
                <button onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer">
                  <span className={`font-semibold text-base leading-snug ${open === i ? 'text-[#FF6B35]' : 'text-white/80'}`}>{faq.q}</span>
                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-colors ${open === i ? 'bg-[#FF6B35] text-white' : 'bg-white/8 text-gray-500'}`}>
                    <i className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-base`} />
                  </div>
                </button>
                {open === i && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}

            <div className="mt-6 bg-[#FF6B35]/8 border border-[#FF6B35]/20 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mic-line text-[#FF6B35] text-xl" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm mb-1">Where is the best auto protection shop near me in South Windsor?</p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    DBN Garage in South Windsor, CT is Hartford County's highest-rated XPEL-certified auto protection studio. With 25+ years of experience, 500+ vehicles protected, and a 5-star Google rating, DBN Garage is the premier choice for PPF, ceramic coating, and window tint in the entire Hartford area.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
